/**
 * Hook facade over the orgs Redux slice.
 *
 * Components subscribe via this hook so they don't have to know about
 * the action-creator API. The hook auto-fetches /api/orgs once per
 * mount when the list is empty + not already loading.
 */
import { useEffect } from 'react';

import type { OrgListing } from '@/features/org/api/orgs.api';
import {
  selectOrgs,
  selectActiveSlug,
  selectActiveOrg,
  selectMyRole,
  selectOrgsLoading,
} from '@/features/org/state/orgs.selectors';
import { fetchOrgs, setActiveOrg, createOrg, removeOrg } from '@/features/org/state/orgs.slice';
import { useDispatch, useSelector } from '@/store/hooks';

export function useOrgs() {
  const dispatch = useDispatch();
  const orgs = useSelector(selectOrgs) as OrgListing[];
  const activeSlug = useSelector(selectActiveSlug) as string | null;
  const activeOrg = useSelector(selectActiveOrg) as OrgListing | null;
  const myRole = useSelector(selectMyRole) as string | null;
  const loading = useSelector(selectOrgsLoading) as boolean;

  // Auto-fetch on first mount. Subsequent mounts hit the cache via the
  // store. fetchedAt selector would let us add a TTL refresh later.
  useEffect(() => {
    if (orgs.length === 0 && !loading) {
      // eslint-disable-next-line no-console
      fetchOrgs(dispatch).catch((e) => console.warn('[useOrgs] fetch failed', e));
    }
    // Intentionally only runs on mount — adding orgs/loading would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return {
    orgs,
    activeSlug,
    activeOrg,
    myRole,
    loading,
    switchOrg: (slug: string | null) => setActiveOrg(dispatch, slug),
    createOrg: (input: { name: string; slug?: string }) => createOrg(dispatch, input),
    deleteOrg: (slug: string) => removeOrg(dispatch, slug),
    refresh: () => fetchOrgs(dispatch),
  };
}
